import { animate, style, transition, trigger } from '@angular/animations';
import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { RenderAccountService } from '../render-account.service';
import { RenderActivity, RenderFlowService, RenderReady } from '../render-flow.service';

// The menu keeps this far from the window's edges: the phone's page gutter.
const EDGE_PX = 22;

let nextId = 0;

// The profile button in the landing's and the flow's headers (designer, Figma 28444:20590 "nik",
// 2026-09-25), where the flow had its library icon. A guest gets her blue person icon, and a click
// signs them in. A signed-in user gets the first letter of their name (or of their email), and a
// click opens a small menu (Figma 28732:45719, owner
// 2026-10-20): Library, which carries the count that used to sit on the library icon, and Profile,
// which opens in place on the account's email and Sign Out. The white account popup it used to open
// is gone.
//
// While a model renders, the ring fills as an arc; once a result comes in that the user has not
// opened, a dot lights on the ring (red if the render failed) until the model is opened. The menu's
// Library row gives the percentage, or what is ready. Until 2026-10-20 the result had a pill beside
// the button; it appeared after the page had drawn and pushed the header's links aside (owner).
//
// An inline dropdown, not a mat-menu: styles.scss moves every cdk-overlay-pane.
@Component({
  selector: 'app-render-profile-menu',
  templateUrl: './render-profile-menu.component.html',
  styleUrls: ['./render-profile-menu.component.scss'],
  animations: [
    trigger('slideToggle', [
      transition(':enter', [style({ height: 0, opacity: 0 }), animate('0.2s ease-out', style({ height: '*', opacity: 1 }))]),
      transition(':leave', [style({ height: '*', opacity: 1 }), animate('0.16s ease-in', style({ height: 0, opacity: 0 }))]),
    ]),
  ],
})
export class RenderProfileMenuComponent implements OnInit, AfterViewChecked, OnDestroy {
  // The page the menu leads to that is already open, if any.
  @Input() current: 'library' | null = null;
  // The landing's ink where the first landing had blue (designer, 2026-10-12) — the only tone
  // since that landing went (2026-10-16); the base styles under :host(.is-ink) are its blue.
  @HostBinding('class.is-ink') public readonly isInk = true;

  @ViewChild('trigger') private trigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('menu') private menu?: ElementRef<HTMLElement>;
  @ViewChild('initialMark') private initialMark?: ElementRef<SVGTextElement>;

  public readonly menuId = `render-profile-menu-${nextId++}`;
  public readonly rendered$: Observable<number> = this.flow.renderedCount$;
  public open = false;
  // Profile opened in the menu (email and Sign Out); closed again each time the menu opens.
  public profileOpen = false;
  public readonly reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  // Horizontal nudge that keeps the open menu inside the window.
  public shift = 0;
  // Where the letter is drawn in the ring's 36 × 36 box (see centreInitial): its advance centred,
  // the baseline 6px under the middle — a capital's half height — until the font says exactly.
  public letterAt = { x: 18, y: 24 };

  // A model at work (the ring), and results not opened yet (the dot).
  public activity: RenderActivity | null = null;
  public ready: RenderReady | null = null;

  private centredLetter = '';

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly account: RenderAccountService,
    private readonly flow: RenderFlowService,
    private readonly firebaseAuth: FirebaseAuthService,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    // Firebase restores or ends the session on its own schedule; the button follows it.
    const refresh = (): void => {
      this.zone.run(() => {
        if (!this.isSignedIn) {
          this.open = false;
        }
        this.changeDetector.markForCheck();
      });
    };
    this.subscriptions.add(this.firebaseAuth.firebaseUser.subscribe(refresh));
    this.subscriptions.add(this.firebaseAuth.notLoggedUserSubscription.subscribe(refresh));
    this.subscriptions.add(this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => (this.open = false)));
    this.subscriptions.add(this.flow.activity$.subscribe((activity) => (this.activity = activity)));
    this.subscriptions.add(this.flow.ready$.subscribe((ready) => (this.ready = ready)));
  }

  ngAfterViewChecked(): void {
    const letter = this.isSignedIn ? this.initial : '';
    if (letter && letter !== this.centredLetter && this.initialMark) {
      this.centredLetter = letter;
      this.centreInitial(letter, this.initialMark.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public get isSignedIn(): boolean {
    return this.account.isSignedIn;
  }

  // The name's first letter, else the email's; none (her person icon) when neither is known.
  public get initial(): string {
    const user = this.firebaseAuth.currentUser;
    const source = (user?.displayName || '').trim() || (user?.email || '').trim();
    const first = Array.from(source)[0] || '';
    return first.toLocaleUpperCase();
  }

  // Under Profile: the account's email, else its name (an account with no email).
  public get who(): string {
    const user = this.firebaseAuth.currentUser;
    return (user?.email || '').trim() || (user?.displayName || '').trim();
  }

  public get label(): string {
    if (!this.isSignedIn) {
      return 'Sign in';
    }
    const user = this.firebaseAuth.currentUser;
    const who = (user?.displayName || '').trim() || user?.email || '';
    const busy = this.activity ? `, ${this.activityText.toLowerCase()}` : '';
    const ready = this.ready ? `, ${this.readyText.toLowerCase()}` : '';
    return (who ? `Your account, ${who}` : 'Your account') + busy + ready;
  }

  // The work to show: nothing for a guest, whose jobs are gone.
  public get shownActivity(): RenderActivity | null {
    return this.isSignedIn ? this.activity : null;
  }

  // Ready results to announce: none for a guest either.
  public get shownReady(): RenderReady | null {
    return this.isSignedIn ? this.ready : null;
  }

  public get readyText(): string {
    return this.ready ? RenderFlowService.readyText(this.ready) : '';
  }

  // Under Library in the menu: the work in progress, else what is waiting to be opened.
  public get menuNote(): string {
    return this.shownActivity ? this.activityText : this.shownReady ? this.readyText : '';
  }

  // "Rendering · 45%", "Uploading · 4%", or "2 rendering · 45%" (on average) — the ring's number.
  public get activityText(): string {
    const activity = this.activity;
    if (!activity) {
      return '';
    }
    const verb = activity.uploading ? 'uploading' : 'rendering';
    return `${activity.count > 1 ? `${activity.count} ${verb}` : verb[0].toUpperCase() + verb.slice(1)} · ${activity.progress}%`;
  }

  public onTriggerClick(): void {
    if (!this.isSignedIn) {
      this.account.openAuthPopup();
      return;
    }
    if (this.open) {
      this.close();
      return;
    }
    this.open = true;
    this.profileOpen = false;
    this.shift = 0;
    this.changeDetector.detectChanges();
    this.keepInWindow();
    this.items()[0]?.focus();
  }

  public toggleProfile(): void {
    this.profileOpen = !this.profileOpen;
  }

  public onSignOut(): void {
    this.close(false);
    this.account.signOut();
  }

  public close(returnFocus = true): void {
    if (!this.open) {
      return;
    }
    this.open = false;
    if (returnFocus) {
      this.trigger?.nativeElement.focus();
    }
  }

  // Arrows, Home and End move between the entries (Sign Out while Profile is open); Tab leaves the
  // menu and closes it.
  public onMenuKeydown(event: KeyboardEvent): void {
    const items = this.items();
    const index = items.indexOf(document.activeElement as HTMLElement);
    let next = -1;
    switch (event.key) {
      case 'ArrowDown':
        next = (index + 1) % items.length;
        break;
      case 'ArrowUp':
        next = (index - 1 + items.length) % items.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
      case 'Tab':
        this.close(false);
        return;
      default:
        return;
    }
    event.preventDefault();
    items[next]?.focus();
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.close(false);
    }
  }

  @HostListener('keydown.escape')
  public onEscape(): void {
    this.close();
  }

  // The letter's ink on the ring's centre (owner, 2026-09-25: the "C" did not sit quite straight).
  // Centring its advance and a capital's nominal height leaves it off by up to half a pixel — the
  // round side of a C, the leg of a K or an R, Outfit's own metrics — so the glyph is measured in the
  // font the ring uses and placed by its ink box.
  private async centreInitial(letter: string, mark: SVGTextElement): Promise<void> {
    const font = getComputedStyle(mark).font;
    await document.fonts?.load(font, letter).catch(() => undefined);
    const context = document.createElement('canvas').getContext('2d');
    if (!context || letter !== this.centredLetter) {
      return;
    }
    context.font = font;
    const ink = context.measureText(letter);
    const x = 18 + (ink.width + ink.actualBoundingBoxLeft - ink.actualBoundingBoxRight) / 2;
    const y = 18 + (ink.actualBoundingBoxAscent - ink.actualBoundingBoxDescent) / 2;
    if (Number.isFinite(x + y)) {
      this.zone.run(() => {
        this.letterAt = { x: +x.toFixed(2), y: +y.toFixed(2) };
        this.changeDetector.markForCheck();
      });
    }
  }

  private items(): HTMLElement[] {
    return Array.from(this.menu?.nativeElement.querySelectorAll<HTMLElement>('[role="menuitem"]') || []);
  }

  // Under the button, its right edge on the button's — unless that puts it past a window edge
  // (the flow's button sits mid-header on a phone).
  private keepInWindow(): void {
    const menu = this.menu?.nativeElement;
    if (!menu) {
      return;
    }
    const box = menu.getBoundingClientRect();
    const width = document.documentElement.clientWidth;
    if (box.left < EDGE_PX) {
      this.shift = EDGE_PX - box.left;
    } else if (box.right > width - EDGE_PX) {
      this.shift = width - EDGE_PX - box.right;
    }
    this.changeDetector.detectChanges();
  }
}
